/**
 * PerChannelSparkline — multi-line overlay chart for the EC + Moisture
 * detail pages. Pulls /api/dashboard/history-by-channel, draws one
 * Recharts line per soil probe, and shows a tappable legend so the user
 * can isolate the dry-outlier zone at a glance.
 *
 * Why an overlay (vs stacked separate charts)?
 *  • 5 lines on one chart let the user *compare* — the whole point.
 *  • Stacked sparklines would tell the same story per-probe in isolation
 *    but lose the "this probe is 13pts below the others" gestalt.
 */
import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceArea } from 'recharts';
import { API_URL } from '../../config/api';

const COLORS = ['#38bdf8', '#a78bfa', '#34d399', '#f59e0b', '#fb7185', '#fb923c', '#22d3ee', '#facc15'];

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `-${Math.max(0, Math.round((Date.now() - d.getTime()) / 3.6e6))}h`;
  } catch { return ''; }
}

export function PerChannelSparkline({
  metric = 'moisture',
  hours = 48,
  unit = '',
  idealMin = null,
  idealMax = null,
  height = 230,
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [hidden, setHidden] = useState(new Set());

  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/dashboard/history-by-channel?metric=${metric}&hours=${hours}`, {
      credentials: 'include',
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(j => { if (live) setData(j); })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [metric, hours]);

  if (err) return (
    <div className="bg-slate-900/80 border border-rose-900/60 rounded-2xl p-4 text-xs text-rose-300">
      Failed to load per-channel history: {err}
    </div>
  );
  if (!data) return (
    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 text-xs text-slate-400 animate-pulse" style={{ minHeight: height }}>
      Loading per-probe data…
    </div>
  );
  if (!data.channels || data.channels.length === 0) return (
    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 text-xs text-slate-400 text-center" style={{ minHeight: height }}>
      <div className="pt-12">No probes are reporting this metric yet.</div>
    </div>
  );

  // Merge every channel's series onto a single time axis so Recharts can
  // line them up. Key = timestamp; row = {t, raw, ch1, ch2, …}.
  const byT = new Map();
  data.channels.forEach((c) => {
    c.series.forEach(({ t, v }) => {
      if (!byT.has(t)) byT.set(t, { raw: t, t: fmtTime(t) });
      byT.get(t)[`ch${c.channel}`] = v;
    });
  });
  const chartData = Array.from(byT.values()).sort((a, b) => a.raw.localeCompare(b.raw));

  // Range padding for Y axis — pull from all visible channels
  const allVals = chartData.flatMap(row =>
    data.channels
      .filter(c => !hidden.has(c.channel))
      .map(c => row[`ch${c.channel}`])
      .filter(v => v != null),
  );
  const pad = Math.max((Math.max(...allVals) - Math.min(...allVals)) * 0.15, 0.5);
  const yMin = Math.floor(Math.min(...allVals) - pad);
  const yMax = Math.ceil(Math.max(...allVals) + pad);

  const toggle = (chNum) => {
    setHidden(prev => {
      const next = new Set(prev);
      next.has(chNum) ? next.delete(chNum) : next.add(chNum);
      return next;
    });
  };

  return (
    <div data-testid={`per-channel-sparkline-${metric}`} className="bg-slate-900/80 border border-slate-800 rounded-2xl p-3" style={{ minHeight: height }}>
      <ResponsiveContainer width="100%" height={height - 70}>
        <LineChart data={chartData} margin={{ top: 12, right: 12, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" vertical={false} />
          <XAxis dataKey="t" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis domain={[yMin, yMax]} tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} width={32} />
          {idealMin !== null && idealMax !== null && (
            <ReferenceArea y1={idealMin} y2={idealMax} fill="#10b981" fillOpacity={0.05} stroke="#10b981" strokeOpacity={0.3} strokeDasharray="3 3" />
          )}
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#94a3b8' }}
            formatter={(v, name) => [`${v}${unit}`, name.toUpperCase()]}
          />
          {data.channels.map((c, idx) => hidden.has(c.channel) ? null : (
            <Line
              key={c.channel}
              type="monotone"
              dataKey={`ch${c.channel}`}
              stroke={COLORS[idx % COLORS.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3, stroke: '#0f172a', strokeWidth: 2 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Tappable legend — click a chip to toggle the channel on/off */}
      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-slate-800" data-testid="per-channel-legend">
        {data.channels.map((c, idx) => {
          const off = hidden.has(c.channel);
          return (
            <button
              key={c.channel}
              data-testid={`channel-chip-${c.channel}`}
              onClick={() => toggle(c.channel)}
              className={`text-[10px] font-medium px-2 py-1 rounded-full border transition-opacity ${off ? 'opacity-40' : 'opacity-100'}`}
              style={{
                color: COLORS[idx % COLORS.length],
                borderColor: COLORS[idx % COLORS.length] + '55',
                background: COLORS[idx % COLORS.length] + '12',
              }}
            >
              <span className="font-bold">Ch {c.channel}</span>
              {c.last != null && <> · {c.last}{unit}</>}
              {c.sensor_model && <span className="ml-1 text-slate-400">({c.sensor_model})</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
